import { APP_NAME } from '../../config/appConstants';
import AuthPageFooter from '../../shared/components/common/AuthPageFooter';
import './KickedOutPage.css';
import appLogo from '../../assets/images/DevicePulse-app-logo2.png';

/**
 * KickedOutPage Component
 *
 * Displayed when user is logged out due to:
 * - Browser refresh (sessionStorage cleared)
 * - Session expiration
 * - Logout from another device/tab
 * - Admin session termination
 * - Token blacklisted
 *
 * Matches Login page theme with organization name, logo, and DevicePulse branding
 */
function KickedOutPage({ reason = 'session_expired', loginUrl = '/login' }) {

  // Map reason codes to user-friendly messages
  const reasonMessages = {
    session_expired: {
      title: 'Session Expired',
      message: 'Your session has expired due to inactivity or page refresh. Please log in again to continue.',
      icon: '⏱️'
    },
    session_closed: {
      title: 'Session Closed',
      message: 'Your session has been closed. Please log in again to continue.',
      icon: '🔌'
    },
    logged_out_other_device: {
      title: 'Logged Out',
      message: 'You were logged out from another device or browser window. Please log in again.',
      icon: '📱'
    },
    admin_logout: {
      title: 'Logged Out by Admin',
      message: 'An administrator has logged you out. Please log in again if you have been granted access.',
      icon: '⛔'
    },
    token_invalid: {
      title: 'Invalid Session',
      message: 'Your session is no longer valid. Please log in again.',
      icon: '🔒'
    },
    default: {
      title: 'Logged Out',
      message: 'You have been logged out. Please log in again to continue.',
      icon: '👋'
    }
  };

  const { title, message, icon } = reasonMessages[reason] || reasonMessages.default;

  const handleLogin = () => {
    // Clear the kicked out reason from sessionStorage
    try {
      sessionStorage.removeItem('kicked_out_reason');
    } catch (e) {
      console.warn('[KICKEDOUT_PAGE] Failed to clear kicked_out_reason from sessionStorage:', e);
    }

    // Use window.location instead of React Router since we need to reset the entire app state
    // This will cause a full page reload and unmount the KickedOutPage component
    // The App component will then show the Login page
    window.location.href = loginUrl;
  };

  return (
    <div className="kicked-out-container">
      {/* Left Side - Animated Gradient Background (matches Login) */}
      <div className="kicked-out-left-side">
        {/* Background Layers */}
        <div className="particles"></div>
        <div className="medical-pattern"></div>

        {/* Floating Medical Device Illustrations */}
        <div className="medical-float-1">
          <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="35" y="20" width="50" height="70" rx="5" fill="white" opacity="0.9"/>
            <rect x="30" y="85" width="60" height="25" rx="3" fill="white" opacity="0.9"/>
            <circle cx="60" cy="55" r="15" fill="rgba(102,126,234,0.3)"/>
            <rect x="55" y="30" width="10" height="15" fill="rgba(102,126,234,0.4)"/>
            <line x1="40" y1="95" x2="80" y2="95" stroke="rgba(102,126,234,0.5)" strokeWidth="2"/>
          </svg>
        </div>

        <div className="medical-float-2">
          <svg width="140" height="100" viewBox="0 0 140 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <ellipse cx="70" cy="50" rx="60" ry="35" fill="white" opacity="0.9"/>
            <ellipse cx="70" cy="50" rx="30" ry="20" fill="rgba(102,126,234,0.3)"/>
            <rect x="10" y="40" width="15" height="20" rx="3" fill="white" opacity="0.8"/>
            <rect x="115" y="40" width="15" height="20" rx="3" fill="white" opacity="0.8"/>
            <circle cx="70" cy="30" r="5" fill="rgba(102,126,234,0.5)"/>
          </svg>
        </div>

        {/* Left Side Content */}
        <div className="left-side-content">
          <div className="left-side-text">
            <h2>Welcome to</h2>
            <h1>
              <img src={appLogo} className="emoji-app-logo" alt="App Logo" />
              {APP_NAME}
            </h1>
            <p>Manage your medical devices with ease and confidence</p>
          </div>
        </div>

      </div>

      {/* Right Side - Kicked Out Message (matches Login layout) */}
      <div className="kicked-out-right-side">
        <AuthPageFooter />

        <div className="kicked-out-box">
          <div className="kicked-out-header">
            <div className="kicked-out-icon">{icon}</div>
            <h3>{title}</h3>
          </div>

          <p className="kicked-out-message">{message}</p>

          <div className="kicked-out-reasons">
            <p className="kicked-out-subtext">This may happen because:</p>
            <ul>
              <li>Your browser was refreshed</li>
              <li>You logged out from another window</li>
              <li>Your session has expired</li>
              <li>You closed the browser and reopened it</li>
              <li>There was a security reason to end your session</li>
            </ul>
          </div>

          <button
            type="button"
            className="action-btn action-primary action-btn-block kicked-out-login-btn"
            onClick={handleLogin}
          >
            Log In Again
          </button>

          <div className="kicked-out-footer-note">
            <p className="kicked-out-footer-text">
              If you believe this is an error, please contact your administrator.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default KickedOutPage;
